/**
 * LinkedInConnectionStatus
 * 
 * Displays connected LinkedIn accounts with actions:
 * - Verify token validity
 * - Disconnect account
 * - Refresh metadata
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Loader2, CheckCircle, XCircle, RefreshCw, Trash2, Lock, Share2, HelpCircle } from 'lucide-react';
import { EmptyState } from '../ui/empty-state';
import { fetchWithAuth } from '../../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LinkedInConnectionStatus({ onRefresh = null }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await fetchWithAuth('/api/social/linkedin/accounts');

      if (!response.ok) {
        throw new Error('Failed to fetch accounts');
      }

      const data = await response.json();

      if (data.success) {
        setAccounts(data.data.accounts || []);
      }
    } catch (err) {
      console.error('Failed to fetch LinkedIn accounts:', err);
      toast.error('Impossible de récupérer les comptes LinkedIn');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (tokenId) => {
    setActionLoading(tokenId);
    try {
      const response = await fetchWithAuth(
        `/api/social/linkedin/verify?token_id=${tokenId}`,
        { method: 'POST', body: JSON.stringify({}) }
      );

      if (!response.ok) {
        throw new Error('Verification failed');
      }

      const data = await response.json();

      if (data.success) {
        toast.success('Compte LinkedIn vérifié avec succès');
      } else {
        toast.error('Le token a expiré ou est invalide');
      }

      await fetchAccounts();
    } catch (err) {
      console.error('Verification error:', err);
      toast.error('Erreur lors de la vérification');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisconnect = async (tokenId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir déconnecter ce compte LinkedIn ?')) {
      return;
    }

    setActionLoading(tokenId);
    try {
      const response = await fetchWithAuth(
        `/api/social/linkedin/disconnect?token_id=${tokenId}`,
        { method: 'POST', body: JSON.stringify({}) }
      );

      if (!response.ok) {
        throw new Error('Disconnect failed');
      }

      const data = await response.json();

      if (data.success) {
        toast.success('Compte LinkedIn déconnecté');
        await fetchAccounts();
        if (onRefresh) onRefresh();
      } else {
        toast.error('Erreur lors de la déconnexion');
      }
    } catch (err) {
      console.error('Disconnect error:', err);
      toast.error('Erreur lors de la déconnexion');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRefreshMetadata = async (tokenId) => {
    setActionLoading(tokenId);
    try {
      const response = await fetchWithAuth(
        `/api/social/linkedin/refresh?token_id=${tokenId}`,
        { method: 'POST', body: JSON.stringify({}) }
      );

      if (!response.ok) {
        throw new Error('Refresh failed');
      }

      const data = await response.json();

      if (data.success) {
        toast.success('Métadonnées rafraîchies');
        await fetchAccounts();
      } else {
        toast.error('Erreur lors du rafraîchissement');
      }
    } catch (err) {
      console.error('Refresh error:', err);
      toast.error('Erreur lors du rafraîchissement');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="linkedin-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <EmptyState
        icon={<Lock size={48} />}
        title="Connectez LinkedIn 💼"
        message="Publiez sur LinkedIn directement depuis NEXORA AI. Partagez votre contenu professionnel avec votre réseau sans quitter la plateforme."
        primaryAction={{
          label: "Connecter LinkedIn",
          onClick: () => window.location.href = '/social-accounts',
          icon: <Share2 size={20} />
        }}
        secondaryAction={{
          label: "En savoir plus sur OAuth",
          onClick: () => {
            toast.success("OAuth 2.0 : Vos identifiants restent privés. Connexion sécurisée sans stocker vos mots de passe.");
          },
          variant: "outline",
          icon: <HelpCircle size={20} />
        }}
        variant="info"
        size="default"
        animated={true}
        testId="empty-linkedin"
        footer={
          <p className="text-sm text-zinc-500">
            🔒 Connexion sécurisée via OAuth 2.0 — Vos données restent protégées
          </p>
        }
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="linkedin-accounts-list">
      {accounts.map((account) => (
        <Card 
          key={account.token_id}
          className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors"
          data-testid={`linkedin-account-${account.token_id}`}
        >
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-blue-600/20 rounded-lg">
                  <svg 
                    className="w-8 h-8 text-blue-500" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-semibold text-lg">
                      {account.account_name}
                    </h3>
                    <Badge 
                      variant={account.is_valid ? 'success' : 'destructive'}
                      className="text-xs"
                      data-testid={`linkedin-status-${account.token_id}`}
                    >
                      {account.is_valid ? (
                        <>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Actif
                        </>
                      ) : (
                        <>
                          <XCircle className="w-3 h-3 mr-1" />
                          Expiré
                        </>
                      )}
                    </Badge>
                  </div>
                  
                  <p className="text-zinc-400 text-sm mt-1 capitalize">
                    {account.account_type?.replace('_', ' ') || 'LinkedIn'}
                  </p>
                  
                  {account.account_id && (
                    <p className="text-zinc-500 text-xs mt-1">
                      ID: {account.account_id}
                    </p>
                  )}
                  
                  {account.connected_at && (
                    <p className="text-zinc-500 text-xs mt-1">
                      Connecté le {new Date(account.connected_at).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleVerify(account.token_id)}
                disabled={actionLoading === account.token_id}
                className="bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 border-blue-500/20"
                data-testid={`verify-btn-${account.token_id}`}
              >
                {actionLoading === account.token_id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Vérifier
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRefreshMetadata(account.token_id)}
                disabled={actionLoading === account.token_id}
                className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border-zinc-700"
                data-testid={`refresh-btn-${account.token_id}`}
              >
                {actionLoading === account.token_id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Rafraîchir
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDisconnect(account.token_id)}
                disabled={actionLoading === account.token_id}
                className="bg-red-600/10 hover:bg-red-600/20 text-red-400 border-red-500/20"
                data-testid={`disconnect-btn-${account.token_id}`}
              >
                {actionLoading === account.token_id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Déconnecter
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
